import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import { createContactNavigationHandler } from '../../../utils/contactNavigation';

import {
  StyledAppBar,
  StyledContainer,
  StyledToolbar,
  MobileMenuContainer,
  DesktopNavContainer,
  DesktopLogo,
  NavContactIcons,
  ContactIconButton,
  ContactIconAvatar,
  MobileLogo,
  NavButton,
  StyledMenu,
  StyledMenuItem,
  StyledMenuButton,
  StyledIconButton,
} from './TopNav.styles';

function TopNav({ pages }) {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const menuButtonRef = useRef(null);
  const navigate = useNavigate();
  const handleContactClick = createContactNavigationHandler(navigate);

  const handleOpenNavMenu = event => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = useCallback(() => {
    setAnchorElNav(null);
    // Return focus to menu button when menu closes
    if (menuButtonRef.current) {
      setTimeout(() => {
        menuButtonRef.current.focus();
      }, 100);
    }
  }, []);

  // Handle Escape key to close menu
  useEffect(() => {
    const handleEscape = event => {
      if (event.key === 'Escape' && anchorElNav) {
        handleCloseNavMenu();
      }
    };

    if (anchorElNav) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [anchorElNav, handleCloseNavMenu]);

  return (
    <StyledAppBar
      position="static"
      component="nav"
      id="main-navigation"
      role="navigation"
    >
      <StyledContainer maxWidth="false">
        <StyledToolbar disableGutters>
          <MobileMenuContainer>
            <StyledIconButton
              ref={menuButtonRef}
              size="large"
              aria-label="Open navigation menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              aria-expanded={Boolean(anchorElNav)}
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </StyledIconButton>
            <StyledMenu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
            >
              {pages.map(page => (
                <StyledMenuItem key={page.slug} onClick={handleCloseNavMenu}>
                  <StyledMenuButton
                    key={page.title}
                    component={Link}
                    to={page.path}
                  >
                    {page.title}
                  </StyledMenuButton>
                </StyledMenuItem>
              ))}
            </StyledMenu>
          </MobileMenuContainer>
          <DesktopLogo component={Link} to="/" variant="h1">
            Hadley Swello
          </DesktopLogo>
          <NavContactIcons>
            <ContactIconButton
              component="button"
              onClick={handleContactClick}
              aria-label="Email Hadley Swello"
            >
              <ContactIconAvatar
                alt=""
                src="email-icon.png"
                aria-hidden="true"
              />
            </ContactIconButton>
            <ContactIconButton
              component="a"
              href="https://www.linkedin.com/in/hadleyswello/"
              target="_blank"
              rel="noopener"
              aria-label="Hadley Swello on LinkedIn"
            >
              <ContactIconAvatar
                alt=""
                src="linkedin-icon.png"
                variant="square"
                aria-hidden="true"
              />
            </ContactIconButton>
            <ContactIconButton
              component="a"
              href="https://github.com/HadleySwello"
              target="_blank"
              rel="noopener"
              aria-label="Hadley Swello on GitHub"
            >
              <ContactIconAvatar
                alt=""
                src="github-logo.svg"
                variant="square"
                aria-hidden="true"
              />
            </ContactIconButton>
          </NavContactIcons>
          <MobileLogo variant="h1" component={Link} to="/">
            <span>Hadley</span>
            <span>Swello</span>
          </MobileLogo>
          <DesktopNavContainer>
            {pages.map(page => (
              <NavButton
                variant="primary"
                key={page.title}
                component={Link}
                to={page.path}
              >
                {page.title}
              </NavButton>
            ))}
          </DesktopNavContainer>
        </StyledToolbar>
      </StyledContainer>
    </StyledAppBar>
  );
}
export default TopNav;
